/* Ngăn hồ sơ khách tiềm năng (Telesales): thông tin, thẻ S, nhu cầu, 5 bước từ API + kết quả, lịch hẹn, nhắn tin lưu trên máy */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Modal, Tabs, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import {
  CalendarPlus, Check, Copy, Eye, EyeOff, Maximize2, MessageCircle, MessagesSquare, Minimize2, Minus, Pause, Phone, PhoneOff, Play, X,
} from 'lucide-react';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import type { CustomerTagItem } from '../../../api/CustomerTag';
import { getWorkSteps, postCallLog, reloadQuotes, reloadTouches, saveCustomerNeeds, saveCustomerTag } from './api';
import CallResult, { type CallResultValue } from './CallResult';
import type { Booking } from './BookingFields';
import { MsgTab, NeedsTab, TouchTab } from './LeadTabs';
import ApptTab from './ApptTab';
import WorkHistoryPanel from '../../CustomerDetails/components/WorkHistoryPanel';
import { isDemoId } from './demo';
import { suggestType } from './appt';
import QuoteTab from './QuoteTab';
import PhotoTab, { LeadAvatar } from './PhotoTab';
import { ensureDemoPhotos } from './demoPhotos';
import {
  EMPTY_LOCAL, activeAppt, isDepositTag, type NextStep, chatLinksOf, isConnected, textOn, codeLabel, dueText, findTag, maskPhone, mangOf, mergeSteps, mmss, nextStep, shortTag, sourceOf, staffOf, tagOf, when, workSteps, xungHo,
  type Kenh, type LeadAppt, type LeadLocal, type Mang, type ServerWorkStep, type WorkStepKey,
} from './logic';
import { currentStaffName, useLeadStore } from './store';
import { ChatTagPicker, CsStatusPicker, type PickItem } from './TagPicker';

/** answer = nghe cuộc gọi đến từ Tổng đài Sales */
export type LeadAction = 'call' | 'answer' | 'msg' | 'appt' | 'needs';
/** Giới hạn hoàn tất phiếu sau khi gác máy (giây) — cùng ngưỡng ACW của dashboard */
const ACW_SEC = 5 * 60;

interface CallState { sec: number; phase: 'talk' | 'acw'; hold: boolean; acw: number; dir: 'out' | 'in' }

const NEXT_TONE = {
  missed: 'Cuộc nhỡ', callback: 'Hẹn gọi lại', confirm: 'Xác nhận lịch', appt: 'Lịch hẹn', new: 'Khách mới', retry: 'Gọi lại', none: '',
} as const;

export default function LeadDrawer({ record: r, tags, initial, canNext, onNext, onClose, onCallChange, readOnly, expanded = false, onExpandedChange, careStatuses = [], canApprove }: {
  record: ChannelCustomerItem;
  /** Trực page / Sale cơ sở: chỉ xem lịch sử, lịch hẹn và bổ sung nhu cầu — không gọi, không đặt lịch */
  readOnly?: boolean;
  /** Chế độ mở rộng do trang giữ, để mở khách khác vẫn giữ nguyên */
  expanded?: boolean;
  onExpandedChange?: (v: boolean) => void;
  tags: CustomerTagItem[];
  /** Danh sách thẻ chat để gắn / bỏ trong hồ sơ */
  careStatuses?: PickItem[];
  /** Người vận hành duyệt báo giá giảm vượt ngưỡng */
  canApprove?: boolean;
  initial?: LeadAction;
  canNext: boolean;
  onNext: () => void;
  onClose: () => void;
  onCallChange: (active: boolean) => void;
}) {
  const l = useLeadStore((s) => s.byId[r.id]) ?? EMPTY_LOCAL;
  const { patch, addTouch, addAppt } = useLeadStore();
  const by = useMemo(currentStaffName, []);
  const [view, setView] = useState<'normal' | 'expanded' | 'min'>(expanded ? 'expanded' : 'normal');
  // Bung lại từ thu nhỏ → về đúng cỡ người dùng đã chọn (thường / mở rộng)
  const restore = expanded ? 'expanded' : 'normal';
  const [tab, setTab] = useState<string>(initial === 'call' || initial === 'answer' || initial === 'msg' || !initial ? 'touch' : initial);
  // Mẫu tin Zalo mở trong popup (tab cũ "Nhắn tin" nay là Lịch sử công việc)
  const [zalo, setZalo] = useState(initial === 'msg' && !readOnly);
  const [call, setCall] = useState<CallState | null>(null);
  const [showPhone, setShowPhone] = useState(false);
  // Mỗi lần bấm "Đặt lịch" tăng lên để tab Lịch hẹn mở form tạo lịch
  const [apptOpen, setApptOpen] = useState(initial === 'appt' ? 1 : 0);
  // Báo giá được chọn khi bấm "Đặt lịch theo báo giá"; bấm "Đặt lịch" thường thì form tự lấy báo giá mới nhất
  const [apptQuote, setApptQuote] = useState<string>();
  // Bấm bước "Báo giá & cọc" khi khách chưa có báo giá → mở luôn form tạo báo giá
  const [quoteOpen, setQuoteOpen] = useState(0);
  // Lịch vừa đặt trong phiếu kết quả cuộc gọi cần đưa lên hệ thống
  const [syncReq, setSyncReq] = useState<{ id: string; n: number }>();
  const openAppt = (quoteId?: string) => { setApptQuote(quoteId); setTab('appt'); setApptOpen((n) => n + 1); };
  const [now, setNow] = useState(Date.now());

  const tag = tagOf(r, l, tags);
  const mang = mangOf(r, l);
  const src = sourceOf(r);
  const chats = chatLinksOf(r);
  const xung = xungHo(r.gender);
  const name = (r.display_name || r.fullname || 'Khách').trim();
  const onlyMsg = l.kenh === 'Chỉ nhắn tin';
  const [serverSteps, setServerSteps] = useState<ServerWorkStep[]>();
  const [stepsTick, setStepsTick] = useState(0);
  const steps = mergeSteps(workSteps(l, mang, tag?.name), serverSteps);
  const baseStep = nextStep(r, l, now, tag?.name);
  // Khách đã cọc mà chưa có lịch: nhắc Telesales đặt lịch. Theo bước của server vì lịch trên máy có thể chưa đủ
  const step: NextStep = baseStep.kind === 'none' && isDepositTag(tag?.name) && serverSteps?.some((s) => s.key === 'appt' && !s.done)
    ? { kind: 'appt', label: 'Đặt lịch cho khách đã cọc' }
    : baseStep;
  const curStep = steps.findIndex((s) => !s.done && !s.optional);

  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(id); }, []);
  // Tải 5 bước khi mở hồ sơ và sau mỗi thao tác; khách mẫu chỉ tính trên máy
  useEffect(() => {
    if (isDemoId(r.id)) return undefined;
    let stale = false;
    getWorkSteps(r.id).then((res) => { if (!stale && res.ok && res.data) setServerSteps(res.data.steps); });
    return () => { stale = true; };
  }, [r.id, tag?.id, l.touches.length, l.appts, l.quotes, stepsTick]);
  useEffect(() => { setServerSteps(undefined); }, [r.id]);
  // Lịch sử chạm, báo giá trên server: đổi máy vẫn thấy, tab Lịch hẹn cũng cần báo giá để đặt lịch theo
  useEffect(() => {
    if (isDemoId(r.id)) return;
    reloadTouches(r.id);
    reloadQuotes(r.id, { id: r.id, name, code: r.customer_code || `KH${r.id}`, phone: r.phone });
  }, [r.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Nạp mảng và phiếu nhu cầu trên server vào phiếu, tách needs.kenh ra kênh khách muốn
  useEffect(() => {
    if (isDemoId(r.id) || (!r.mang && !r.needs)) return;
    const { kenh, ...needs } = (r.needs ?? {}) as NonNullable<LeadLocal['needs']> & { kenh?: Kenh };
    patch(r.id, {
      ...(r.mang ? { mang: r.mang as Mang } : {}),
      ...(r.needs ? { needs: { ...l.needs, ...needs } } : {}),
      ...(kenh ? { kenh } : {}),
    });
  }, [r.id, r.mang, r.needs]); // eslint-disable-line react-hooks/exhaustive-deps
  // Khách mẫu: có sẵn ảnh khách gửi để xem tab Hình ảnh
  useEffect(() => { ensureDemoPhotos(r, l, mang, patch); }, [r.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!call) return undefined;
    const id = setInterval(() => setCall((c) => {
      if (!c) return c;
      if (c.phase === 'acw') return { ...c, acw: c.acw - 1 };
      return c.hold ? c : { ...c, sec: c.sec + 1 };
    }), 1000);
    return () => clearInterval(id);
  }, [call !== null]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onCallChange(!!call); }, [call !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCall = (dir: 'out' | 'in' = 'out') => {
    if (call) return;
    // Khách tự gọi đến thì nghe luôn, không chặn theo kênh khách muốn
    if (dir === 'out' && !r.phone) { message.warning('Khách chưa có số điện thoại'); return; }
    if (dir === 'out' && onlyMsg) { message.warning('Khách chỉ muốn nhắn tin — dùng Zalo'); setZalo(true); return; }
    setCall({ sec: 0, phase: 'talk', hold: false, acw: ACW_SEC, dir });
    setTab('call');
    setView((v) => (v === 'min' ? restore : v));
  };
  // Mở từ nút "Gọi" trên danh sách → gọi luôn; "Nghe" cuộc gọi đến → vào cuộc gọi đến
  useEffect(() => { if ((initial === 'call' || initial === 'answer') && !readOnly) startCall(initial === 'answer' ? 'in' : 'out'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hangup = () => setCall((c) => (c ? { ...c, phase: 'acw', hold: false } : c));
  const tryClose = () => {
    if (call) { message.warning('Đang có cuộc gọi chưa lưu kết quả — lưu kết quả trước khi đóng.'); setTab('call'); setView(restore); return; }
    onClose();
  };
  const closeRef = React.useRef(tryClose);
  closeRef.current = tryClose;
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      // Esc trong ô nhập / ô chọn chỉ đóng ô đó, không đóng ngăn hồ sơ
      const el = e.target as HTMLElement | null;
      if (e.key !== 'Escape' || el?.closest?.('input, textarea, .ant-select, .ant-picker, .ant-modal-wrap, .ant-popover')) return;
      closeRef.current();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  /** auto = tự đổi theo lịch hẹn (S5, S6): ma trận chặn đổi tay nên giữ luồng cũ, không gọi API đổi thẻ */
  const changeTag = async (tagId?: number, auto = false) => {
    if (!tagId || tagId === tag?.id) return;
    const t = tags.find((x) => x.id === tagId);
    if (auto || isDemoId(r.id)) patch(r.id, { tagId });
    else {
      const res = await saveCustomerTag(r.id, tagId);
      if (!res.ok) { message.error(res.message || 'Không đổi được Trạng thái CS'); return; }
    }
    addTouch(r.id, { kind: 'stage', text: `Mã S ${shortTag(tag?.name) || 'chưa có'} → ${shortTag(t?.name)}`, by });
  };

  /** Mảng, phiếu đổi ngoài tab Nhu cầu cũng phải lên server, không thì lần mở sau server ghi đè */
  const pushNeeds = (nextMang: Mang | undefined, needs: LeadLocal['needs']) => {
    if (isDemoId(r.id)) return;
    saveCustomerNeeds(r.id, { mang: nextMang, needs: { ...needs, ...(l.kenh ? { kenh: l.kenh } : {}) } }).then((res) => {
      if (!res.ok) message.warning(`Chưa lưu được nhu cầu lên máy chủ: ${res.message}`);
    });
  };

  const book = (b: Booking, extra?: Partial<LeadAppt>, keepTag = false, needs = l.needs) => {
    const a = addAppt(r.id, {
      at: b.at!.valueOf(), branch: b.branch!, mang: b.mang!, service: b.service || '', note: b.note.trim(), type: b.type ?? suggestType(l.appts), by,
      cust: { name, phone: r.phone, code: r.customer_code },
      ...extra,
    });
    addTouch(r.id, { kind: 'appt', text: `Đặt lịch ${dayjs(a.at).format('HH:mm DD/MM')} · ${a.branch} · ${a.type} · ${a.mang}`, by });
    patch(r.id, { mang: b.mang, callback: undefined });
    if (b.mang !== r.mang) pushNeeds(b.mang, needs);
    const cur = shortTag(tag?.name).toUpperCase();
    // Khách đã cọc giữ S7.1 khi đặt lịch (PA)
    if (!keepTag && !/^S[56]/.test(cur) && !isDepositTag(cur)) changeTag(findTag(tags, 'S5')?.id, true);
    message.success(`Đã đặt lịch ${dayjs(a.at).format('HH:mm DD/MM')} tại ${a.branch}`);
    return a;
  };

  const saveResult = (v: CallResultValue, next: boolean) => {
    addTouch(r.id, { kind: 'call', dir: call?.dir ?? 'out', dur: call?.sec ?? 0, code: v.code, text: v.note, by });
    // Lý do hỏi được trong cuộc gọi → cập nhật phiếu nhu cầu
    const lyDoChanged = v.lyDo !== l.needs?.ly_do;
    const needs = lyDoChanged ? { ...l.needs, ly_do: v.lyDo } : l.needs;
    patch(r.id, { callback: v.callback, ...(lyDoChanged ? { needs } : {}) });
    // Ghi nhật ký để server tính bước Gọi & lưu phiếu; số bị che thì server không nhận được
    if (!isDemoId(r.id) && r.phone && !String(r.phone).includes('*')) {
      postCallLog({ phone: r.phone, direction: call?.dir ?? 'out', answered: isConnected(v.code), duration: call?.sec ?? 0, note: v.note }).then((res) => {
        if (res.ok) setStepsTick((n) => n + 1);
        else message.warning(`Chưa lưu được nhật ký cuộc gọi lên máy chủ: ${res.message}`);
      });
    }
    const booked = v.booking ? book(v.booking, undefined, true, needs) : undefined;
    if (lyDoChanged && (!v.booking || v.booking.mang === r.mang)) pushNeeds(mang, needs);
    changeTag(v.tagId, !!v.booking);
    setCall(null);
    message.success(`Đã lưu ${v.code} ${codeLabel(v.code)}`);
    // Lịch đặt trong cuộc gọi mới lưu trên máy: mở luôn form đưa lên hệ thống, không thì Lễ tân / kiosk không check-in được
    if (booked && !isDemoId(r.id)) {
      setTab('appt');
      setSyncReq((s) => ({ id: booked.id, n: (s?.n ?? 0) + 1 }));
      message.info('Kiểm tra thông tin khách rồi bấm “Đưa lên hệ thống” để Lễ tân / kiosk check-in được.');
      return;
    }
    setTab('touch');
    if (next) setTimeout(onNext, 0);
  };

  /** Bấm một bước → mở đúng tab / thao tác của bước đó */
  const goStep = (k: WorkStepKey) => {
    if (k === 'needs') setTab(k);
    else if (k === 'quote') {
      setTab(k);
      if (!readOnly && !steps.find((s) => s.key === 'quote')?.done) setQuoteOpen((n) => n + 1);
    }
    else if (k === 'call') {
      if (call) setTab('call');
      else if (readOnly) setTab('touch');
      else startCall();
    } else {
      if (k === 'appt' && !readOnly && !activeAppt(l, now)) openAppt();
      else setTab('appt');
    }
  };

  const copyPhone = () => {
    if (!r.phone) return;
    navigator.clipboard?.writeText(r.phone).then(() => message.success('Đã chép số điện thoại'), () => undefined);
  };

  /* ---------- Thu nhỏ ---------- */
  if (view === 'min') {
    return (
      <div className="fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-full bg-[#2c6b7f] py-2 pl-4 pr-2 text-white shadow-2xl">
        <span className={`h-2.5 w-2.5 rounded-full ${call ? 'animate-pulse bg-red-400' : 'bg-emerald-400'}`} />
        <span className="text-sm font-semibold">{name}{call ? ` · ${call.phase === 'talk' ? mmss(call.sec) : 'Chờ lưu kết quả'}` : ''}</span>
        <button type="button" onClick={() => setView(restore)} className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold hover:bg-white/25">Mở lại</button>
        <button type="button" onClick={tryClose} className="rounded-full p-1 hover:bg-white/20" aria-label="Đóng"><X size={16} /></button>
      </div>
    );
  }

  const due = step.due ? dueText(step.due, now) : null;
  const width = view === 'expanded' ? 'xl:w-[52vw]' : 'xl:w-[34vw] xl:min-w-[440px]';

  const tabItems = [
    ...(call ? [{ key: 'call', label: <span className="font-semibold text-red-500">Cuộc gọi</span>, children: (
      <CallResult xung={xung} source={src} mang={mang} tags={tags} curTag={tag} canNext={canNext} inbound={call.dir === 'in'} lyDo={l.needs?.ly_do} onSave={saveResult} />
    ) }] : []),
    { key: 'touch', label: `Lịch sử chạm${l.touches.length ? ` (${l.touches.length})` : ''}`, children: <TouchTab r={r} l={l} now={now} /> },
    { key: 'needs', label: 'Nhu cầu', children: <NeedsTab key={`${l.mang}-${l.kenh}-${l.needs?.ly_do ?? ''}`} r={r} l={l} by={by} /> },
    { key: 'photo', label: `Hình ảnh${l.photos?.length ? ` (${l.photos.length})` : ''}`, children: <PhotoTab r={r} l={l} by={by} mang={mang} readOnly={readOnly} /> },
    { key: 'quote', label: `Báo giá & cọc${l.quotes?.length ? ` (${l.quotes.length})` : ''}`, children: <QuoteTab r={r} l={l} by={by} xung={xung} now={now} mang={mang} readOnly={readOnly} canApprove={canApprove} openSignal={quoteOpen} onBook={openAppt} /> },
    { key: 'appt', label: `Lịch hẹn${l.appts.length ? ` (${l.appts.length})` : ''}`, children: <ApptTab r={r} l={l} by={by} now={now} xung={xung} mang={mang} readOnly={readOnly} openSignal={apptOpen} syncSignal={syncReq} quoteId={apptQuote} onBooked={book} confirmTagId={isDepositTag(tag?.name) ? undefined : findTag(tags, 'S6')?.id} onConfirmed={() => { if (/^S5/.test(shortTag(tag?.name).toUpperCase())) changeTag(findTag(tags, 'S6')?.id, true); }} /> },
    {
      key: 'work',
      label: 'Lịch sử công việc',
      // Cùng ô bình luận với hồ sơ khách hàng (/customer-details) — khách mẫu chưa có trên hệ thống
      children: isDemoId(r.id)
        ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Khách mẫu — chưa có lịch sử công việc trên hệ thống" />
        : <WorkHistoryPanel customerId={r.id} hideFilters />,
    },
  ];

  const info: [string, React.ReactNode][] = [
    ['Telesales', staffOf(r, l)?.name || <span className="text-orange-500">Chưa phân</span>],
    ['Trực page tạo', r.in_charge_staff?.name || '—'],
    ['Nguồn', src || '—'],
    ['Mảng', mang || <button type="button" onClick={() => setTab('needs')} className="text-[#3e91ab] hover:underline">Chọn mảng</button>],
    ['Tỉnh / TP', r.province?.name || '—'],
    ['Kênh khách muốn', l.kenh || 'Chưa hỏi'],
    ['Thẻ thanh toán', r.has_received_deposit ? <span className="font-semibold text-emerald-600">Cọc</span> : '—'],
  ];

  return (
    <aside className={`fixed inset-0 z-[41] flex flex-col overflow-hidden bg-white text-sm shadow-2xl dark:bg-gray-800 xl:sticky xl:inset-auto xl:top-[72px] xl:z-[29] xl:h-[calc(100vh-96px)] xl:shrink-0 xl:rounded-xl xl:border xl:border-gray-200 xl:shadow-sm xl:dark:border-gray-700 ${width}`}>
      {/* Thanh điều khiển */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
        <span className="text-[13px] font-semibold text-gray-500 dark:text-gray-400">Hồ sơ khách tiềm năng</span>
        <div className="flex items-center gap-1">
          <Tooltip title="Thu nhỏ"><button type="button" aria-label="Thu nhỏ" onClick={() => setView('min')} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"><Minus size={16} /></button></Tooltip>
          <Tooltip title={view === 'expanded' ? 'Thu gọn' : 'Mở rộng'}>
            <button type="button" aria-label={view === 'expanded' ? 'Thu gọn' : 'Mở rộng'} onClick={() => { const next = view === 'expanded' ? 'normal' : 'expanded'; setView(next); onExpandedChange?.(next === 'expanded'); }} className="hidden rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 xl:block">
              {view === 'expanded' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          </Tooltip>
          <Tooltip title="Đóng (Esc)"><button type="button" aria-label="Đóng" onClick={tryClose} className="rounded-md p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10"><X size={17} /></button></Tooltip>
        </div>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {/* Định danh */}
        <div className="flex items-start gap-3">
          <LeadAvatar leadId={r.id} photoId={l.avatar} name={name} size={44} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[16px] font-bold text-gray-900 dark:text-gray-50">{name}</span>
              <span className="text-[12px] text-gray-500">xưng {xung}</span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-gray-500">
              <span className="font-semibold text-gray-700 dark:text-gray-300">{r.customer_code || `KH-${r.id}`}</span>
              <span>·</span>
              <span className="tabular-nums text-gray-700 dark:text-gray-300">{showPhone ? r.phone : maskPhone(r.phone) || 'Chưa có SĐT'}</span>
              {r.phone && (
                <>
                  <Tooltip title={showPhone ? 'Ẩn số' : 'Hiện số'}><button type="button" aria-label={showPhone ? 'Ẩn số' : 'Hiện số'} onClick={() => setShowPhone((v) => !v)} className="text-gray-400 hover:text-[#3e91ab]">{showPhone ? <EyeOff size={14} /> : <Eye size={14} />}</button></Tooltip>
                  <Tooltip title="Chép số"><button type="button" aria-label="Chép số" onClick={copyPhone} className="text-gray-400 hover:text-[#3e91ab]"><Copy size={13} /></button></Tooltip>
                </>
              )}
            </div>
          </div>
          <div className="shrink-0 text-right">
            {tag ? (
              <Tooltip title={tag.local ? `${tag.name} · đổi trên máy này, chờ đồng bộ` : tag.name}>
                <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-bold" style={{ background: tag.color || '#64748b', color: textOn(tag.color) }}>
                  {shortTag(tag.name)}{tag.local && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />}
                </span>
              </Tooltip>
            ) : <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[12px] font-semibold text-gray-500 dark:bg-gray-700">Chưa có mã</span>}
            {r.created_at && <div className="mt-1 text-[11px] text-gray-400">Tạo {dayjs(r.created_at).format('DD/MM HH:mm')}</div>}
          </div>
        </div>

        {/* Thông tin */}
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-[12.5px] dark:border-gray-700 dark:bg-gray-900/40 sm:grid-cols-2">
          {info.map(([k, v]) => (
            <div key={k} className="flex min-w-0 gap-1.5"><span className="shrink-0 text-gray-500">{k}:</span><span className="min-w-0 font-medium text-gray-800 dark:text-gray-200">{v}</span></div>
          ))}
          <div className="col-span-full flex min-w-0 items-center gap-1.5">
            <span className="shrink-0 text-gray-500">Trạng thái CS:</span>
            <CsStatusPicker r={r} tags={tags} />
          </div>
          <div className="col-span-full flex min-w-0 items-start gap-1.5">
            <span className="shrink-0 pt-0.5 text-gray-500">Thẻ chat:</span>
            <ChatTagPicker r={r} all={careStatuses} />
          </div>
          {r.note && <div className="col-span-full flex gap-1.5"><span className="shrink-0 text-gray-500">Ghi chú trực page:</span><span className="font-medium text-gray-800 dark:text-gray-200">{r.note}</span></div>}
        </div>

        {/* Việc tiếp theo */}
        <div className={`flex items-center justify-between gap-3 rounded-lg border-l-4 px-3 py-2.5 ${due?.late ? 'border-red-500 bg-red-50 dark:bg-red-500/10' : step.kind === 'none' ? 'border-gray-300 bg-gray-50 dark:bg-gray-900/40' : 'border-[#3e91ab] bg-[#e8f4f7] dark:bg-[#3e91ab]/10'}`}>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Việc tiếp theo{step.kind !== 'none' ? ` · ${NEXT_TONE[step.kind]}` : ''}</div>
            <div className="text-[13.5px] font-semibold text-gray-800 dark:text-gray-100">{step.kind === 'none' ? 'Không có việc đang chờ' : step.label}</div>
            {l.touches[0] && <div className="text-[11.5px] text-gray-500">Lần chạm cuối: {when(l.touches[0].at, now)}{l.touches[0].code ? ` · ${l.touches[0].code}` : ''}</div>}
          </div>
          {due && <span className={`shrink-0 text-[13px] font-bold ${due.late ? 'text-red-600' : 'text-[#2c6b7f] dark:text-[#8cc8da]'}`}>{due.text}</span>}
        </div>

        {/* Các bước với khách — bước đầu chưa xong là chỗ bắt đầu */}
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            Các bước với khách{curStep >= 0 ? <span className="normal-case tracking-normal text-[#2c6b7f] dark:text-[#8cc8da]"> · bắt đầu ở bước {curStep + 1}</span> : <span className="normal-case tracking-normal text-emerald-600"> · đã xong</span>}
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {steps.map((s, i) => {
              const cur = i === curStep;
              // Bước đang mở ở tab bên dưới
              const open = s.key === tab;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => goStep(s.key)}
                  aria-current={open ? 'step' : undefined}
                  className={`min-w-0 rounded-lg border px-2 py-1.5 text-left transition-colors ${open && !cur ? 'ring-2 ring-[#3e91ab]/40' : ''} ${cur
                    ? 'border-[#3e91ab] bg-[#e8f4f7] ring-1 ring-[#3e91ab] dark:bg-[#3e91ab]/15'
                    : s.done ? 'border-emerald-200 bg-emerald-50/60 hover:border-emerald-400 dark:border-emerald-500/30 dark:bg-emerald-500/10' : s.optional ? 'border-[#3e91ab]/40 bg-white hover:border-[#3e91ab] hover:bg-[#e8f4f7]/60 dark:bg-transparent' : 'border-gray-200 hover:border-[#3e91ab] dark:border-gray-700'}`}
                >
                  <div className="flex items-start gap-1.5 text-[12px] font-semibold leading-tight text-gray-800 dark:text-gray-100">
                    <span className={`mt-px flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${s.done ? 'bg-emerald-500 text-white' : cur ? 'bg-[#3e91ab] text-white' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {s.done ? <Check size={11} strokeWidth={3} /> : i + 1}
                    </span>
                    <span>{s.label}</span>
                  </div>
                  <div className={`mt-0.5 truncate text-[11px] ${cur || (s.optional && !s.done && !readOnly) ? 'font-semibold text-[#2c6b7f] dark:text-[#8cc8da]' : 'text-gray-500'}`}>{cur ? 'Bắt đầu ở đây' : s.optional && !s.done && !readOnly ? '+ Tạo báo giá' : s.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Thao tác */}
        {(!readOnly || chats.length > 0) && (
          <div className="flex flex-wrap gap-2">
            {!readOnly && (
              <>
                <Tooltip title={onlyMsg ? 'Khách chỉ muốn nhắn tin' : !r.phone ? 'Chưa có số điện thoại' : ''}>
                  <Button type="primary" icon={<Phone size={14} />} disabled={!!call || onlyMsg || !r.phone} onClick={() => startCall()}className="!bg-[#3e91ab] font-semibold disabled:!bg-gray-200">Gọi khách</Button>
                </Tooltip>
                <Button icon={<MessageCircle size={14} />} onClick={() => setZalo(true)}>Zalo</Button>
              </>
            )}
            {/* Vào phòng chat của khách (Facebook / Zalo / TikTok) — khách tạo từ form không có */}
            {chats.map((c) => (
              <Tooltip key={c.key} title={c.url ? `Mở phòng chat${c.page ? ` · ${c.page}` : ''} ở tab mới` : 'Chưa liên kết phòng chat trên hệ thống'}>
                <Button icon={<MessagesSquare size={14} />} disabled={!c.url} onClick={() => c.url && window.open(c.url, '_blank', 'noopener')}>
                  Chat {c.label}
                </Button>
              </Tooltip>
            ))}
            {!readOnly && <Button icon={<CalendarPlus size={14} />} onClick={() => openAppt()}>Đặt lịch</Button>}
          </div>
        )}

        <Tabs size="small" activeKey={tab} onChange={setTab} items={tabItems} />
        {!readOnly && (
          <Modal open={zalo} title="Nhắn Zalo" footer={null} width={640} destroyOnClose onCancel={() => setZalo(false)}>
            <MsgTab r={r} l={l} xung={xung} by={by} now={now} />
          </Modal>
        )}
      </div>

      {/* Tổng đài mềm */}
      {call && (
        <div className="fixed bottom-4 left-1/2 z-[60] w-[300px] -translate-x-1/2 overflow-hidden rounded-2xl bg-gray-900 text-white shadow-2xl ring-1 ring-white/10 xl:left-[calc(50%-17vw)]">
          <div className="flex items-center justify-between px-4 pt-3 text-[12px]">
            <span className="flex items-center gap-1.5 font-semibold">
              <span className={`h-2 w-2 rounded-full ${call.phase === 'acw' ? 'bg-amber-400' : call.hold ? 'bg-sky-400' : 'animate-pulse bg-red-500'}`} />
              {call.phase === 'acw' ? 'Đã gác máy' : call.hold ? 'Đang giữ máy' : call.dir === 'in' ? 'Cuộc gọi đến' : 'Đang gọi'}
            </span>
            <button type="button" onClick={() => { setTab('call'); setView(restore); }} className="text-white/60 hover:text-white">Phiếu kết quả</button>
          </div>
          <div className="px-4 pb-4 pt-2 text-center">
            <div className="text-[15px] font-semibold">{name}</div>
            <div className="text-[12px] tabular-nums text-white/60">{maskPhone(r.phone)}</div>
            <div className="my-2 text-3xl font-bold tabular-nums tracking-wider">{mmss(call.sec)}</div>
            {call.phase === 'talk' ? (
              <div className="flex justify-center gap-3">
                <Tooltip title={call.hold ? 'Tiếp tục' : 'Giữ máy'}>
                  <button type="button" aria-label={call.hold ? 'Tiếp tục' : 'Giữ máy'} onClick={() => setCall((c) => (c ? { ...c, hold: !c.hold } : c))} className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/20">
                    {call.hold ? <Play size={18} /> : <Pause size={18} />}
                  </button>
                </Tooltip>
                <Tooltip title="Gác máy">
                  <button type="button" aria-label="Gác máy" onClick={hangup} className="flex h-11 w-11 items-center justify-center rounded-full bg-red-500 hover:bg-red-600"><PhoneOff size={18} /></button>
                </Tooltip>
              </div>
            ) : (
              <div className={`rounded-lg px-3 py-1.5 text-[12px] font-medium ${call.acw < 0 ? 'bg-red-500/90' : 'bg-amber-500/90'}`}>
                {call.acw >= 0 ? `Lưu kết quả trong ${mmss(call.acw)}` : `Quá hạn lưu kết quả ${mmss(-call.acw)}`}
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}
